import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Clock, Home, Calendar, Search,
  ChevronLeft, ChevronRight, AlertTriangle,
  Check, Settings, Palmtree
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import LeaveBalanceSheet from '../components/LeaveBalanceSheet';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';
const DEPARTMENTS = ["Founder's Office", "Social & Content", "Video & Motion", "Creative & Design", "Paid & Performance"];
const STATUSES = ["Present", "On Leave", "WFH", "Out on Shoot"];
const LEAVE_TYPES = ["Sick Leave", "Casual Leave", "Earned Leave", "Comp-Off"];

export default function AttendancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = user?.role === 'admin' || user?.effective_permissions?.attendance === 'edit';
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaveBanks, setLeaveBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [teamWfh, setTeamWfh] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [lateThreshold, setLateThreshold] = useState('10:00');

  // Overview state
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewStart, setOverviewStart] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [overviewEnd, setOverviewEnd] = useState(() => new Date().toISOString().split('T')[0]);
  const [overviewDept, setOverviewDept] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [attendanceRes, statsRes, leaveBanksRes, teamWfhRes, settingsRes] = await Promise.all([
        axios.get(`${API}/attendance`, { withCredentials: true, params: { date: selectedDate, department: departmentFilter && departmentFilter !== 'all' ? departmentFilter : undefined } }),
        axios.get(`${API}/attendance/stats`, { withCredentials: true, params: { date: selectedDate } }),
        axios.get(`${API}/leave-banks`, { withCredentials: true }),
        axios.get(`${API}/attendance/team-wfh`, { withCredentials: true, params: { date: selectedDate } }),
        axios.get(`${API}/attendance/settings`, { withCredentials: true }),
      ]);
      setAttendance(attendanceRes.data);
      setStats(statsRes.data);
      setLeaveBanks(leaveBanksRes.data);
      setTeamWfh(teamWfhRes.data.enabled);
      setLateThreshold(settingsRes.data.late_threshold);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally { setLoading(false); }
  }, [selectedDate, departmentFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleToggleTeamWfh = async (enabled) => {
    try {
      await axios.post(`${API}/attendance/team-wfh`, { date: selectedDate, enabled }, { withCredentials: true });
      setTeamWfh(enabled);
      toast.success(enabled ? 'Team WFH enabled for this day' : 'Team WFH disabled');
      fetchData();
    } catch (err) { toast.error('Failed to toggle Team WFH'); }
  };

  const handleStatusChange = async (employeeId, status) => {
    const record = attendance.find(a => a.employee_id === employeeId);
    if (!record) return;
    setAttendance(prev => prev.map(a => a.employee_id === employeeId ? { ...a, status, check_in_time: (status === 'Present' || status === 'Out on Shoot') ? a.check_in_time : null, leave_type: null, punctuality: null } : a));
    if (status === 'Present') return; // wait for time input
    if (status === 'Out on Shoot') return; // wait for time input (treated like Present)
    if (status === 'On Leave') return; // wait for leave type
    // For WFH, save immediately
    setSaving(prev => ({ ...prev, [employeeId]: true }));
    try {
      await axios.post(`${API}/attendance`, { employee_id: employeeId, date: selectedDate, status, check_in_time: null, leave_type: null }, { withCredentials: true });
      await refreshStats();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(prev => ({ ...prev, [employeeId]: false })); }
  };

  const handleTimeChange = async (employeeId, time) => {
    setAttendance(prev => prev.map(a => a.employee_id === employeeId ? { ...a, check_in_time: time } : a));
    if (!time) return;
    const record = attendance.find(a => a.employee_id === employeeId);
    const currentStatus = record?.status || 'Present';
    setSaving(prev => ({ ...prev, [employeeId]: true }));
    try {
      const res = await axios.post(`${API}/attendance`, { employee_id: employeeId, date: selectedDate, status: currentStatus, check_in_time: time, leave_type: null }, { withCredentials: true });
      setAttendance(prev => prev.map(a => a.employee_id === employeeId ? { ...a, punctuality: res.data.punctuality } : a));
      await refreshStats();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(prev => ({ ...prev, [employeeId]: false })); }
  };

  const handleLeaveTypeChange = async (employeeId, leaveType) => {
    setAttendance(prev => prev.map(a => a.employee_id === employeeId ? { ...a, leave_type: leaveType } : a));
    setSaving(prev => ({ ...prev, [employeeId]: true }));
    try {
      await axios.post(`${API}/attendance`, { employee_id: employeeId, date: selectedDate, status: 'On Leave', check_in_time: null, leave_type: leaveType }, { withCredentials: true });
      await refreshStats();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(prev => ({ ...prev, [employeeId]: false })); }
  };

  const refreshStats = async () => {
    const [statsRes, leaveBanksRes] = await Promise.all([
      axios.get(`${API}/attendance/stats`, { withCredentials: true, params: { date: selectedDate } }),
      axios.get(`${API}/leave-banks`, { withCredentials: true }),
    ]);
    setStats(statsRes.data);
    setLeaveBanks(leaveBanksRes.data);
  };

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await axios.get(`${API}/attendance/overview`, {
        withCredentials: true,
        params: { start_date: overviewStart, end_date: overviewEnd, department: overviewDept && overviewDept !== 'all' ? overviewDept : undefined }
      });
      setOverviewData(res.data);
    } catch (err) { toast.error('Failed to load overview'); }
    finally { setOverviewLoading(false); }
  }, [overviewStart, overviewEnd, overviewDept]);

  const handleSaveSettings = async (newThreshold) => {
    try {
      await axios.put(`${API}/attendance/settings`, { late_threshold: newThreshold }, { withCredentials: true });
      setLateThreshold(newThreshold);
      toast.success('Late threshold updated');
      setShowSettings(false);
      fetchData();
    } catch (err) { toast.error('Failed to save settings'); }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const isSunday = new Date(selectedDate).getDay() === 0;
  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  if (loading && !stats) {
    return (<SidebarLayout title="Attendance" subtitle="Attendance & Leave Tracker"><div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div></SidebarLayout>);
  }

  return (
    <SidebarLayout title="Attendance" subtitle="Attendance & Leave Tracker">
      {/* KPIs */}
      <section className="mb-8 animate-fadeIn">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard icon={<Check className="text-emerald-400" />} label="On Time" value={stats?.on_time || 0} color="emerald" testId="kpi-on-time" />
          <KPICard icon={<Clock className="text-amber-400" />} label="Late" value={stats?.late || 0} color="amber" testId="kpi-late" />
          <KPICard icon={<Palmtree className="text-cyan-400" />} label="On Leave" value={stats?.on_leave || 0} color="cyan" testId="kpi-on-leave" />
          <KPICard icon={<Home className="text-indigo-400" />} label="WFH" value={stats?.wfh || 0} color="indigo" testId="kpi-wfh" />
          <KPICard icon={<Users className="text-white" />} label="Present" value={stats?.present || 0} color="slate" testId="kpi-present" />
          <KPICard icon={<Calendar className="text-slate-400" />} label="Unmarked" value={stats?.unmarked || 0} color="slate" testId="kpi-unmarked" />
        </div>
      </section>

      {/* Alerts */}
      {stats?.low_balance_alerts?.length > 0 && (
        <section className="mb-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <div className="alert-card border-l-4 border-l-amber-500" data-testid="low-balance-alerts">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="text-amber-400" size={18} />
              <h3 className="font-semibold text-white">Low Leave Balance Alerts</h3>
              <span className="ml-auto text-xs bg-amber-500/20 px-2 py-0.5 rounded-full text-amber-400">{stats.low_balance_alerts.length}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {stats.low_balance_alerts.map((alert, idx) => (
                <div key={idx} className="bg-white/5 px-3 py-2 rounded-lg text-sm">
                  <span className="text-white font-medium">{alert.employee_name}</span>
                  <span className="text-amber-400 ml-2">{alert.alerts.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Date Selector + Filters + Team WFH + Settings */}
      <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => changeDate(-1)} className="text-slate-400 hover:text-white hover:bg-white/5" data-testid="prev-date"><ChevronLeft size={20} /></Button>
            <div className="flex items-center gap-2">
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="bg-black/40 border-white/10 text-white w-[160px]" data-testid="date-picker" />
              {isToday && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Today</span>}
              {isSunday && <span className="text-xs bg-slate-500/20 text-slate-400 px-2 py-1 rounded">Sunday Off</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => changeDate(1)} className="text-slate-400 hover:text-white hover:bg-white/5" data-testid="next-date"><ChevronRight size={20} /></Button>
            <span className="text-slate-400 text-sm hidden lg:block ml-2">{formatDate(selectedDate)}</span>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            {canWrite && !isSunday && (
              <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10">
                <span className="text-xs text-slate-400">Team WFH</span>
                <Switch checked={teamWfh} onCheckedChange={handleToggleTeamWfh} data-testid="team-wfh-toggle" />
              </div>
            )}
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[170px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map(dept => <SelectItem key={dept} value={dept}>{dept}</SelectItem>)}
              </SelectContent>
            </Select>
            {canWrite && (
              <Button variant="ghost" size="icon" onClick={() => setShowSettings(true)} className="text-slate-400 hover:text-white" data-testid="settings-btn"><Settings size={18} /></Button>
            )}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <Tabs defaultValue="attendance" className="animate-fadeIn" style={{ animationDelay: '0.3s' }}>
        <TabsList className="bg-white/5 border border-white/10 mb-6">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-indigo-600" data-testid="tab-attendance">Daily Attendance</TabsTrigger>
          <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-600" data-testid="tab-overview" onClick={fetchOverview}>Overview</TabsTrigger>
          <TabsTrigger value="leave-balances" className="data-[state=active]:bg-indigo-600" data-testid="tab-leave-balances">Leave Balances</TabsTrigger>
        </TabsList>

        {/* Daily Attendance Tab */}
        <TabsContent value="attendance">
          {isSunday ? (
            <div className="glass-card p-12 text-center"><Calendar size={48} className="text-slate-500 mx-auto mb-4" /><h3 className="text-xl text-white font-semibold mb-2">Sunday - Mandatory Off</h3><p className="text-slate-400">Attendance cannot be marked on Sundays</p></div>
          ) : (
            <div className="glass-card overflow-hidden">
              {teamWfh && <div className="bg-indigo-500/10 border-b border-indigo-500/20 px-4 py-2 text-xs text-indigo-400 font-medium flex items-center gap-2"><Home size={14} /> Team WFH Day - WFH will not count against individual balance</div>}
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="attendance-table">
                  <thead><tr><th>Name</th><th>Department</th><th>Status</th><th>Details</th><th>Punctuality</th><th></th></tr></thead>
                  <tbody>
                    {attendance.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-500">No employees found</td></tr>
                    ) : attendance.map((record) => (
                      <tr key={record.employee_id} data-testid={`attendance-row-${record.employee_id}`}>
                        <td><div><div className="text-white font-medium">{record.employee_name}</div><div className="text-xs font-mono text-cyan-400">{record.employee_id}</div></div></td>
                        <td className="text-slate-400">{record.department}</td>
                        <td>
                          <Select value={record.status || ''} onValueChange={(v) => handleStatusChange(record.employee_id, v)} disabled={!canWrite}>
                            <SelectTrigger className={`w-[130px] bg-black/40 border-white/10 ${getStatusColor(record.status)} ${!canWrite ? 'opacity-60' : ''}`} data-testid={`status-${record.employee_id}`}><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent className="bg-[#0f0f15] border-white/10">
                              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          {(record.status === 'Present' || record.status === 'Out on Shoot') && (
                            <Input type="time" value={record.check_in_time || ''} onChange={(e) => handleTimeChange(record.employee_id, e.target.value)} className="w-[120px] bg-black/40 border-white/10 text-white font-mono" data-testid={`checkin-${record.employee_id}`} disabled={!canWrite} />
                          )}
                          {record.status === 'On Leave' && (
                            <Select value={record.leave_type || ''} onValueChange={(v) => handleLeaveTypeChange(record.employee_id, v)} disabled={!canWrite}>
                              <SelectTrigger className="w-[140px] bg-black/40 border-white/10 text-slate-300" data-testid={`leave-type-${record.employee_id}`}><SelectValue placeholder="Leave Type" /></SelectTrigger>
                              <SelectContent className="bg-[#0f0f15] border-white/10">{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                          {record.status === 'WFH' && (
                            <span className="text-xs px-2 py-1 rounded bg-indigo-500/10 text-indigo-400">{record.is_team_wfh ? 'Team WFH' : 'Individual WFH'}</span>
                          )}
                          {!record.status && <span className="text-slate-500 text-xs">-</span>}
                        </td>
                        <td>
                          {(record.status === 'Present' || record.status === 'Out on Shoot') && record.punctuality && (
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${record.punctuality === 'On Time' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`} data-testid={`punctuality-${record.employee_id}`}>{record.punctuality}</span>
                          )}
                        </td>
                        <td>{saving[record.employee_id] ? <span className="text-xs text-amber-400">Saving...</span> : record.status ? <Check size={16} className="text-emerald-400" /> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="glass-card p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">From</label>
                <Input type="date" value={overviewStart} onChange={(e) => setOverviewStart(e.target.value)} className="bg-black/40 border-white/10 text-white w-[150px]" data-testid="overview-start" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">To</label>
                <Input type="date" value={overviewEnd} onChange={(e) => setOverviewEnd(e.target.value)} className="bg-black/40 border-white/10 text-white w-[150px]" data-testid="overview-end" />
              </div>
              <Select value={overviewDept} onValueChange={setOverviewDept}>
                <SelectTrigger className="w-[170px] bg-black/40 border-white/10 text-slate-300"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10"><SelectItem value="all">All</SelectItem>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={fetchOverview} className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="load-overview">Load Overview</Button>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4">
              {[{ c: 'bg-emerald-500', l: 'On Time' }, { c: 'bg-amber-500', l: 'Late' }, { c: 'bg-rose-500', l: 'On Leave' }, { c: 'bg-indigo-500', l: 'WFH' }, { c: 'bg-slate-600', l: 'Team WFH / Sunday' }, { c: 'bg-white/10', l: 'Unmarked' }].map(x => (
                <div key={x.l} className="flex items-center gap-1.5 text-xs text-slate-400"><div className={`w-3 h-3 rounded-sm ${x.c}`} />{x.l}</div>
              ))}
            </div>
          </div>

          {overviewLoading ? (
            <div className="text-center py-12 text-slate-400">Loading overview...</div>
          ) : overviewData ? (
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table text-xs" data-testid="overview-table">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-[#0a0a0f] z-10 min-w-[160px]">Employee</th>
                      {overviewData.dates.map(d => {
                        const dt = new Date(d.date + 'T00:00:00');
                        const dayName = dt.toLocaleDateString('en-IN', { weekday: 'short' });
                        const dayNum = dt.getDate();
                        return <th key={d.date} className={`text-center min-w-[36px] px-1 ${d.is_sunday ? 'text-slate-600' : ''}`}><div>{dayName}</div><div>{dayNum}</div></th>;
                      })}
                      <th className="text-center">Worked</th>
                      <th className="text-center">Late</th>
                      <th className="text-center">Leaves</th>
                      <th className="text-center">WFH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overviewData.employees.map(emp => (
                      <tr key={emp.employee_id}>
                        <td className="sticky left-0 bg-[#0a0a0f] z-10"><div className="text-white font-medium text-xs">{emp.employee_name}</div><div className="text-[10px] text-slate-500">{emp.department}</div></td>
                        {overviewData.dates.map(d => {
                          const day = emp.days[d.date] || { color: 'none' };
                          return <td key={d.date} className="text-center px-1"><OverviewCell day={day} /></td>;
                        })}
                        <td className="text-center font-mono text-sm"><span className="text-white font-bold">{emp.summary.days_worked}</span><span className="text-slate-500">/{emp.summary.available}</span></td>
                        <td className="text-center text-amber-400 font-mono text-sm">{emp.summary.late}</td>
                        <td className="text-center text-rose-400 font-mono text-sm">{emp.summary.on_leave}</td>
                        <td className="text-center text-indigo-400 font-mono text-sm">{emp.summary.wfh}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="glass-card p-12 text-center text-slate-400">Select a date range and click "Load Overview"</div>
          )}
        </TabsContent>

        {/* Leave Balances Tab */}
        <TabsContent value="leave-balances">
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="leave-balances-table">
                <thead><tr><th>Name</th><th>Department</th><th>Sick Leave</th><th>Casual Leave</th><th>Earned Leave</th><th>WFH Balance</th></tr></thead>
                <tbody>
                  {leaveBanks.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-slate-500">No leave balances found</td></tr>
                  ) : leaveBanks.map((bank) => (
                    <tr key={bank.employee_id} className="cursor-pointer" onClick={() => setSelectedEmployee(bank)} data-testid={`leave-bank-row-${bank.employee_id}`}>
                      <td><div><div className="text-white font-medium">{bank.employee_name}</div><div className="text-xs font-mono text-cyan-400">{bank.employee_id}</div></div></td>
                      <td className="text-slate-400">{bank.department}</td>
                      <td><LeaveBalance value={bank.sick_leave} max={8} /></td>
                      <td><LeaveBalance value={bank.casual_leave} max={8} /></td>
                      <td><LeaveBalance value={bank.earned_leave} max={8} /></td>
                      <td><WfhBalance allowed={bank.wfh_allowed || 1} used={bank.wfh_used} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {selectedEmployee && <LeaveBalanceSheet employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} onUpdate={fetchData} />}

      {/* Settings Dialog */}
      {showSettings && <SettingsDialog threshold={lateThreshold} onSave={handleSaveSettings} onClose={() => setShowSettings(false)} />}
    </SidebarLayout>
  );
}

// ===================== HELPER COMPONENTS =====================

function getStatusColor(status) {
  return { 'Present': 'text-emerald-400', 'On Leave': 'text-cyan-400', 'WFH': 'text-indigo-400', 'Out on Shoot': 'text-purple-400' }[status] || 'text-slate-300';
}

function KPICard({ icon, label, value, color, testId }) {
  const borders = { emerald: 'border-emerald-500/20', amber: 'border-amber-500/20', cyan: 'border-cyan-500/20', indigo: 'border-indigo-500/20', slate: 'border-slate-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${borders[color] || borders.slate}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2"><span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>{icon}</div>
      <div className="font-heading text-2xl font-black tracking-tight text-white">{value}</div>
    </div>
  );
}

function OverviewCell({ day }) {
  const colors = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-rose-500', blue: 'bg-indigo-500', purple: 'bg-purple-500', gray: 'bg-slate-600', none: 'bg-white/5' };
  const bg = colors[day.color] || colors.none;
  return (
    <div className={`w-6 h-6 rounded-sm mx-auto ${bg} cursor-default flex items-center justify-center`} title={day.status + (day.time ? ` (${day.time})` : '')}>
      {day.time && <span className="text-[8px] text-white/80 font-mono leading-none">{day.time.split(':')[0]}</span>}
    </div>
  );
}

function LeaveBalance({ value, max }) {
  const pct = Math.max((value / max) * 100, 0);
  const isLow = value < 2;
  const isNeg = value < 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`font-mono text-sm ${isNeg ? 'text-rose-400' : isLow ? 'text-amber-400' : 'text-white'}`}>{value}</span>
      <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${isNeg ? 'bg-rose-400' : isLow ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${Math.min(Math.max(pct, 5), 100)}%` }} />
      </div>
    </div>
  );
}

function WfhBalance({ allowed, used }) {
  const remaining = allowed - used;
  const isNeg = remaining < 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`font-mono text-sm ${isNeg ? 'text-rose-400' : 'text-white'}`}>{remaining}</span>
      <span className="text-[10px] text-slate-500">/ {allowed}</span>
      {isNeg && <span className="text-[10px] text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">Excess</span>}
    </div>
  );
}

function SettingsDialog({ threshold, onSave, onClose }) {
  const [value, setValue] = useState(threshold);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-white font-heading">Attendance Settings</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Late Threshold Time</label>
            <p className="text-[10px] text-slate-500 mb-2">Employees checking in after this time will be auto-marked as "Late"</p>
            <Input type="time" value={value} onChange={(e) => setValue(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="late-threshold-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button onClick={() => onSave(value)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-settings-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
